import React, { Children, useLayoutEffect, useRef, useState } from 'react'
import { Box, Button, Checkbox, Flex, Message, Text } from '@workwhile/ui'
import { ModalSelect, ModalWithSelect } from 'components/ModalSelect'
import type { MenuListProps } from 'react-select'
import { useAddRequirementModalContextValue } from '../context/AddRequirementModalProvider'

type RequirementOption = ReturnType<
  typeof useAddRequirementModalContextValue
>['state']['options'][number]

const OPTION_HEIGHT = 40
const OVERSCAN = 2

const VirtualizedMenuList = ({
  children,
  focusedOption,
  innerProps,
  innerRef,
  maxHeight,
  options,
}: MenuListProps<RequirementOption, false>) => {
  const items = Children.toArray(children)
  const [scrollTop, setScrollTop] = useState(0)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const assignRef = (node: HTMLDivElement | null) => {
    scrollRef.current = node
    if (typeof innerRef === 'function') {
      innerRef(node)
    } else if (innerRef) {
      innerRef.current = node
    }
  }

  const focusedIndex = options.indexOf(focusedOption)
  const resultKey = options
    .map((option) => ('value' in option ? option.value : option.label))
    .join(',')

  useLayoutEffect(() => {
    setScrollTop(0)
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [resultKey])

  useLayoutEffect(() => {
    const node = scrollRef.current
    if (!node || focusedIndex < 0) {
      return
    }

    const optionTop = focusedIndex * OPTION_HEIGHT
    const optionBottom = optionTop + OPTION_HEIGHT
    const viewBottom = node.scrollTop + node.clientHeight

    if (optionTop < node.scrollTop) {
      node.scrollTop = optionTop
      setScrollTop(optionTop)
    } else if (optionBottom > viewBottom) {
      const nextScrollTop = optionBottom - node.clientHeight
      node.scrollTop = nextScrollTop
      setScrollTop(nextScrollTop)
    }
  }, [focusedIndex])

  const { onScroll, ...restInnerProps } = innerProps
  delete restInnerProps.style

  // when a message is being displayed rather than a list of options, we don't need to virtualize
  if (!Array.isArray(children)) {
    return (
      <Box {...restInnerProps} ref={assignRef}>
        {children}
      </Box>
    )
  }

  const height = Math.min(maxHeight, items.length * OPTION_HEIGHT)
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / OPTION_HEIGHT) - OVERSCAN
  )
  const endIndex = Math.min(
    items.length,
    Math.ceil((scrollTop + height) / OPTION_HEIGHT) + OVERSCAN
  )

  return (
    <Box
      {...restInnerProps}
      ref={assignRef}
      height={height}
      maxHeight={maxHeight}
      overflowY="auto"
      pt={0}
      pb={0}
      onScroll={(event) => {
        onScroll?.(event)
        setScrollTop(event.currentTarget.scrollTop)
      }}
    >
      <Box height={items.length * OPTION_HEIGHT} position="relative">
        <Box
          position="absolute"
          top={startIndex * OPTION_HEIGHT}
          left={0}
          right={0}
        >
          {items.slice(startIndex, endIndex)}
        </Box>
      </Box>
    </Box>
  )
}

const SelectedRequirementDataRow = ({
  label,
  value,
}: {
  label: string
  value: string
}) => (
  <Box>
    <Text fontWeight="bold">{label}</Text>
    <Text>{value}</Text>
  </Box>
)

type AddRequirementModalMainProps = {
  open: boolean
  onClose: () => void
}

export const AddRequirementModalMain = ({
  open,
  onClose,
}: AddRequirementModalMainProps) => {
  const {
    state: {
      search,
      options,
      isSearchFetching,
      existingRequirementIds,
      mutationState: { isPending, isError, errorMessage },
    },
    actions: { setSearch, submit, reset },
  } = useAddRequirementModalContextValue()
  const [selectedOption, setSelectedOption] =
    useState<RequirementOption | null>(null)
  const [mustHave, setMustHave] = useState(false)

  const handleClose = () => {
    setSelectedOption(null)
    setMustHave(false)
    reset()
    onClose()
  }

  const handleAdd = () => {
    if (!selectedOption) {
      return
    }

    submit(
      { requirementId: selectedOption.value, mustHave },
      {
        onSuccess: () => {
          handleClose()
        },
      }
    )
  }

  const selectedRequirement = selectedOption?.requirement

  return (
    <ModalWithSelect
      open={open}
      onClose={handleClose}
      position="top"
      title="Add Requirement to Position"
      loading={isPending}
      customCta={
        <Button variant="primary" loading={isPending} onClick={handleAdd}>
          Add
        </Button>
      }
    >
      <Flex flexDirection="column" width="100%">
        <ModalSelect<RequirementOption>
          key={existingRequirementIds.join(',')}
          block
          isClearable
          isSearchable
          isLoading={isSearchFetching}
          filterOption={null}
          placeholder="Search by ID, name, category or verification type..."
          options={options}
          inputValue={search}
          onInputChange={(value, { action }) => {
            if (action === 'input-change') {
              setSearch(value)
            }
          }}
          value={selectedOption}
          onChange={(option) => {
            setSelectedOption(option)
            setSearch('')
          }}
          noOptionsMessage={() =>
            search.trim()
              ? 'No matching requirements'
              : 'Type to search by ID, name, category or verification type...'
          }
          components={{ MenuList: VirtualizedMenuList }}
          styles={{
            option: (base) => ({
              ...base,
              height: OPTION_HEIGHT,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              boxSizing: 'border-box',
            }),
          }}
        />
      </Flex>

      {selectedRequirement ? (
        <Flex mt={3} flexDirection="column" gap={3}>
          <SelectedRequirementDataRow
            label="Requirement Name (Id)"
            value={`${selectedRequirement.name} (${selectedRequirement.id})`}
          />
          <SelectedRequirementDataRow
            label="Verification Type"
            value={selectedRequirement.verificationType}
          />
          <SelectedRequirementDataRow
            label="Category"
            value={selectedRequirement.category}
          />
        </Flex>
      ) : null}

      <Box mt={3} pt={3} borderTop="1px solid" borderColor="neutrals.100">
        <Text fontSize={2} fontWeight="medium" mb={2}>
          Configurable fields
        </Text>
        <Checkbox value={mustHave} onChange={setMustHave}>
          <Box>
            <Text fontWeight="medium">Must Have</Text>
            <Text color="lightText" fontSize={1}>
              Workers cannot accept a shift without meeting this requirement
            </Text>
          </Box>
        </Checkbox>
      </Box>

      {isError && errorMessage ? (
        <Message variant="error" mt={3} borderRadius="small">
          {errorMessage}
        </Message>
      ) : null}
    </ModalWithSelect>
  )
}
